/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */

import {useEffect} from 'react';

type RefreshListener = () => void;

let refreshListeners: RefreshListener[] = [];

/**
 * This hook should only be used within the device view/editor. 
 * Triggers refreshes when required to the current device-view tab in a
 * pub-sub style.
 * 
 * ```typescript
 * 
 *   import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
 * 
 *   // Publisher: Doesn't pass listener, stores trigger function instead.
 *   const triggerRefresh = useDeviceViewRefresh();
 * 
 *   // Subscriber: Passes a refresh function to be called when a refresh is triggered.
 *   // Note that the result does not need to be stored.
 *   const refresh = () => {
 *     ...
 *   }
 *   useDeviceViewRefresh(refresh);
 * 
 * ```
 * 
 * @param   refreshListener A function to be triggered when a refresh is
 *          required. Defaults to null which doesn't add a listener.
 * @returns A function that triggers all listeners' refreshes.
 */
export function useDeviceViewRefresh(refreshListener: RefreshListener = null): (() => void) {
  useEffect(() => {
    if (refreshListener !== null) {
      refreshListeners.push(refreshListener);
      return () => {
        refreshListeners = refreshListeners.filter((listener) => listener !== refreshListener);
      };
    }
  }, []);

  const triggerRefresh = () => {
    refreshListeners.forEach((refresh) => refresh());
  };

  return triggerRefresh;
}
